// HandoverNote — the delegator's message to the delegate, rendered SAFELY.
//
// The note is user-typed plain text. It is rendered as a React text child
// (escaped — never dangerouslySetInnerHTML) and `whitespace-pre-wrap` keeps the
// writer's line breaks. One component for every surface that shows it: the
// delegate inbox, the delegation detail and the requester's leave history.
import React from 'react';
import { MessageSquareText } from 'lucide-react';

export interface HandoverNoteProps {
  note?: string | null;
  /** e.g. "Message from Amina" / "Your message to Juma". */
  label: string;
  /** Rendered instead of nothing when there is no note (optional). */
  emptyText?: string;
  className?: string;
}

/** Normalise CRLF so a note typed on Windows does not render double breaks. */
export function normaliseNote(note?: string | null): string {
  return (note ?? '').replace(/\r\n?/g, '\n').trim();
}

export const HandoverNote: React.FC<HandoverNoteProps> = ({ note, label, emptyText, className }) => {
  const text = normaliseNote(note);
  if (!text) {
    return emptyText ? <div className={`text-xs text-muted-foreground ${className ?? ''}`}>{emptyText}</div> : null;
  }
  return (
    <div className={`rounded-[10px] border border-border bg-muted/40 px-3 py-2 ${className ?? ''}`} data-testid="handover-note">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MessageSquareText className="w-3.5 h-3.5" aria-hidden />
        {label}
      </div>
      <p className="mt-1 text-sm text-charcoal whitespace-pre-wrap break-words">{text}</p>
    </div>
  );
};

export default HandoverNote;
